import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TIMEZONE = 'America/Chicago';

const timeZoneAliases: Record<string, string> = {
  'Central Time (UTC -5:00)': 'America/Chicago',
  'Central Time (UTC - 5:00)': 'America/Chicago',
  'Eastern Time (UTC -4:00)': 'America/New_York',
  'Eastern Time (UTC - 4:00)': 'America/New_York',
  'Mountain Time (UTC -6:00)': 'America/Denver',
  'Mountain Time (UTC - 6:00)': 'America/Denver',
  'Pacific Time (UTC -7:00)': 'America/Los_Angeles',
  'Pacific Time (UTC - 7:00)': 'America/Los_Angeles',
  'Arizona Time (UTC -7:00)': 'America/Phoenix',
  'Arizona Time (UTC - 7:00)': 'America/Phoenix',
  'Alaska Time (UTC -8:00)': 'America/Anchorage',
  'Alaska Time (UTC - 8:00)': 'America/Anchorage',
  'Hawaii Time (UTC -10:00)': 'Pacific/Honolulu',
  'Hawaii Time (UTC - 10:00)': 'Pacific/Honolulu',
  'Central Standard Time': 'America/Chicago',
  'Eastern Standard Time': 'America/New_York',
  'Mountain Standard Time': 'America/Denver',
  'Pacific Standard Time': 'America/Los_Angeles',
  'US Mountain Standard Time': 'America/Phoenix',
  'Alaskan Standard Time': 'America/Anchorage',
  'Hawaiian Standard Time': 'Pacific/Honolulu',
};

const normalizeTimeZone = (tz?: string) => {
  if (!tz) return DEFAULT_TIMEZONE;
  const mapped = timeZoneAliases[tz] || tz;
  try {
    return dayjs.tz.zone(mapped) ? mapped : DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

const hasExplicitOffset = (value: string) => /[zZ]|[+-]\d{2}:\d{2}$/.test(value);

export function parseDateTimeInTimezone(
  dateTimeString: string,
  tz: string,
  options?: { respectOffset?: boolean },
): dayjs.Dayjs {
  const timeZone = normalizeTimeZone(tz);
  if (!dateTimeString) {
    return dayjs.tz(new Date(Number.NaN), timeZone);
  }
  if (options?.respectOffset && hasExplicitOffset(dateTimeString)) {
    return dayjs(dateTimeString).tz(timeZone);
  }
  return dayjs.tz(dateTimeString, timeZone);
}

export function combineDateTimeInTimezone(date: string, time: string, tz: string): string {
  if (!date || !time) return '';
  const timeZone = normalizeTimeZone(tz);
  const combined = dayjs.tz(`${date} ${time}`, timeZone);
  if (!combined.isValid()) return '';
  return combined.format('YYYY-MM-DDTHH:mm:ss');
}

export function formatDateTimeInTimezone(dateTime: string, tz: string, format = 'MMM D, YYYY h:mm A'): string {
  if (!dateTime) return '';
  const parsed = parseDateTimeInTimezone(dateTime, tz);
  if (!parsed.isValid()) return '';
  return parsed.format(format);
}

export function splitDateTimeInTimezone(dateTime: string, tz: string): { date: string; time: string } {
  if (!dateTime) return { date: '', time: '' };
  const parsed = parseDateTimeInTimezone(dateTime, tz);
  if (!parsed.isValid()) return { date: '', time: '' };
  return {
    date: parsed.format('YYYY-MM-DD'),
    time: parsed.format('HH:mm'),
  };
}

export function getEndOfDayInTimezone(date: string, tz: string): string {
  if (!date) return '';
  const timeZone = normalizeTimeZone(tz);
  const endOfDay = dayjs.tz(date, timeZone).endOf('day');
  if (!endOfDay.isValid()) return '';
  return endOfDay.format('YYYY-MM-DDTHH:mm:ss');
}

export function normalizeTimeZoneId(tz?: string): string {
  return normalizeTimeZone(tz);
}
